import { useMemo, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { useRequest } from 'hooks/api'
import { useAirdropTransactions, useSendRefund } from 'hooks/airdrop'

import {
  useAirdropId,
  useIsSelfGenerated,
  useIsAirdropFailed,
  useIsAirdropPending,
  useIsAirdropSuccess,
  useTransactionsCount,
  useIsAirdropNotStarted,
  useIsAirdropInterrupted,
  useClearPrepareAirdropData,
} from 'state/airdrop/hooks'
import { useIsPendingTopUpTx } from 'state/user/hooks'

import { Button, Icon } from 'components/ui'
import { TxCounter } from 'components/features'

import { Links } from 'constants/link'

import { useOpenModal } from 'modal'
import { newAirdropTxModal } from 'modals/AirdropTx'

import { TextAirdrop, ButtonsWrapAirdrop, IconWrap } from './Style'

type Props = {
  isDisabled: boolean
  withBackButton: boolean
  backToStrategy: VoidFunction
}

export function AirdropContent({ backToStrategy, isDisabled, withBackButton }: Props) {
  const airdropId = useAirdropId()

  const navigate = useNavigate()
  const openModal = useOpenModal()

  const isSelfGenerated = useIsSelfGenerated()
  const transactionsCount = useTransactionsCount()
  const airdropTransactions = useAirdropTransactions()

  const isAirdropFailed = useIsAirdropFailed()
  const isAirdropSuccess = useIsAirdropSuccess()
  const isAirdropPending = useIsAirdropPending()
  const isPendingTopUpTx = useIsPendingTopUpTx()
  const isAirdropNotStarted = useIsAirdropNotStarted()
  const isAirdropInterrupted = useIsAirdropInterrupted()

  const sendRefund = useSendRefund()

  const clearPrepareAirdropData = useClearPrepareAirdropData()

  const [errorMessage, setErrorMessage] = useState('')

  const isAbortion = useMemo(() => {
    return airdropId && isAirdropInterrupted
  }, [airdropId, isAirdropInterrupted])

  const refund = useRequest(sendRefund)

  const onRefund = async () => {
    if (isSelfGenerated) {
      await refund.call()
    }

    backToMainPage()
  }

  const onCreateAirdrop = () => {
    openModal(newAirdropTxModal({ onErrorCallback: (error) => setErrorMessage(error) }))
  }

  const backToMainPage = () => {
    navigate(Links.TOKEN)
    clearPrepareAirdropData()
  }

  const isAirdropStarted = useMemo(() => isPendingTopUpTx || isAirdropPending, [isPendingTopUpTx, isAirdropPending])

  return (
    <>
      {isAirdropSuccess && <Icon name='love' size='xxl' />}
      {isAirdropSuccess && (
        <ButtonsWrapAirdrop>
          <Button fullSize size='large' onClick={backToMainPage}>
            _start-new-sending
          </Button>
        </ButtonsWrapAirdrop>
      )}
      {isAirdropFailed && (
        <>
          <IconWrap>
            <Icon name='sad' size='xxl' />
          </IconWrap>

          <TextAirdrop type='error'>{errorMessage}</TextAirdrop>

          <ButtonsWrapAirdrop>
            <Button fullSize size='large' onClick={backToMainPage}>
              _start-new-sending
            </Button>
          </ButtonsWrapAirdrop>
        </>
      )}
      {isAirdropPending && (
        // ToDo 0xVoushi
        <>
          <IconWrap>
            <Icon name='codeHands' size='xxl' />
          </IconWrap>
          <TxCounter totalTxs={transactionsCount} sentCount={airdropTransactions.transactions?.length} />
        </>
      )}
      {isAirdropNotStarted && (
        <>
          <IconWrap>
            <Icon name='codeHands' size='xxl' />
          </IconWrap>
          <TextAirdrop>
            Above, you have all pertinent details for the airdrop sending you&rsquo;re about to&nbsp;initiate.
          </TextAirdrop>

          <ButtonsWrapAirdrop>
            {withBackButton && (
              <Button fullSize type='outline' size='large' onClick={backToStrategy} disabled={isAirdropStarted}>
                _back
              </Button>
            )}

            <Button fullSize size='large' disabled={isDisabled} onClick={onCreateAirdrop} isLoading={isAirdropStarted}>
              _proceed
            </Button>
          </ButtonsWrapAirdrop>
        </>
      )}
      {isAbortion && (
        <>
          <TextAirdrop type='error'>
            The airdrop was interrupted{isSelfGenerated && <span>, please, make refund and start new sending</span>}.
            You can check already sent transactions in _tx-history modal.
          </TextAirdrop>

          <ButtonsWrapAirdrop>
            <Button fullSize size='large' onClick={onRefund} loadingText='_processing' isLoading={refund.isLoading}>
              {isSelfGenerated && <span>_refund &</span>}_start-new-sending
            </Button>
          </ButtonsWrapAirdrop>
        </>
      )}
    </>
  )
}
